import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { showNotification } from "context/notification.service";
import {
  serviceboxProxy,
  busyNotifier,
  type EstimationResult,
  type PrChoice,
  type ServiceBoxCredentials,
} from "@services/serviceboxProxy";

/** « Veuillez choisir entre la remplacée et la remplaçante » : ServiceBox stops the devis
 *  when a part of the panier has a newer reference. One block per part, the remplaçante
 *  ServiceBox pre-checks stays the default; the pick goes back through the proxy and the
 *  priced devis comes back to `onResult` like any fetch. */
export default function ServiceBoxPrChoiceModal({
  choices,
  creds,
  dossierId,
  onResult,
  onClose,
}: {
  choices: PrChoice[];
  creds: ServiceBoxCredentials;
  dossierId: string;
  onResult: (result: EstimationResult) => Promise<void> | void;
  onClose: () => void;
}) {
  const groups = choices.reduce<Record<string, PrChoice[]>>((acc, c) => {
    (acc[c.group] ||= []).push(c);
    return acc;
  }, {});
  const [sel, setSel] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const c of choices) if (!(c.group in init) || c.selected) init[c.group] = c.value;
    return init;
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const selections = Object.entries(sel).map(([group, value]) => ({ group, value }));
      await onResult(await serviceboxProxy.choosePr(creds, dossierId, selections, busyNotifier()));
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || e?.message || "Erreur" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseModal onClose={onClose} title="Choisir la pièce" startIcon={<Icon1 name="sett-servicebox" size={20} />} width="max-w-lg">
      <div className="flex flex-col gap-3 p-4">
        <span className="text-h3 text-neutral-500">
          Des pièces du panier ont une nouvelle référence. Choisissez celle à mettre sur le devis :
        </span>
        {Object.entries(groups).map(([group, opts]) => (
          <div key={group} className="flex flex-col gap-1.5">
            {opts.map((c) => {
              const checked = sel[group] === c.value;
              return (
                <label
                  key={c.value}
                  className="flex flex-row items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer"
                  style={{ border: checked ? "2px solid #000" : "1.5px solid #E5E5E5", background: checked ? "#F5F5F5" : "#fff" }}
                >
                  <input type="radio" name={`pr-${group}`} checked={checked} onChange={() => setSel((p) => ({ ...p, [group]: c.value }))} />
                  <div className="flex flex-col flex-1">
                    <span className="text-h4 text-neutral-400">{c.kind === "remplacee" ? "Remplacée" : "Remplaçante"}</span>
                    <span className="text-h3 font-medium text-neutral-700">{c.ref} — {c.designation}</span>
                    <span className="text-h4 text-neutral-400">
                      {c.prix_ttc ? `${c.prix_ttc} € TTC` : ""}
                      {c.prix_ht ? ` · ${c.prix_ht} € HT` : ""}
                      {c.stock ? ` · stock ${c.stock}` : ""}
                    </span>
                  </div>
                </label>
              );
            })}
          </div>
        ))}
        <div className="flex flex-row gap-2 pt-1">
          <Button label={submitting ? "Application…" : "Valider et générer le devis"} onClick={submit} disabled={submitting} variant="small" />
          <SecondaryButton label="Annuler" onClick={onClose} variant="small" />
        </div>
      </div>
    </BaseModal>
  );
}
